"use client";

import type { ReactNode } from "react";
import { JsonLd } from "@/components/site/JsonLd";
import { Accordion } from "@/components/ui/accordion";
import { cn } from "@/lib/utils";

type FaqItem = {
  question: string;
  answer: ReactNode;
};

type FAQAccordionProps = {
  items: FaqItem[];
  title?: string;
  className?: string;
  /** When true, renders FAQPage JSON-LD alongside the visual component */
  withJsonLd?: boolean;
  editorial?: boolean;
};

export function FAQAccordion({ items, title, className, withJsonLd = true, editorial = false }: FAQAccordionProps) {
  if (items.length === 0) return null;

  const accordionItems = items.map((item, index) => ({
    id: `faq-${index}`,
    trigger: item.question,
    content: <p className="leading-relaxed">{item.answer}</p>,
  }));

  return (
    <div className={cn("faq-container", className)}>
      {withJsonLd && (
        <JsonLd
          scriptKey="faq-jsonld"
          data={{
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: items.map((item) => ({
              "@type": "Question",
              name: item.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: typeof item.answer === "string" ? item.answer : item.question,
              },
            })),
          }}
        />
      )}
      {title && <h2 className="text-2xl font-semibold text-slate-900 mb-4">{title}</h2>}
      <Accordion items={accordionItems} type="single" itemClassName={editorial ? (isOpen) => isOpen ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-100" : undefined} />
    </div>
  );
}
